import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import API_BASE from '../../../../utils/api.js';
import QuickBooksConnectModal from '../../../utils/QuickbooksConnectModal';
import notify from '../../../../utils/notify';
import { isQuickBooksConnectionError } from '../../../../utils/quickbooksErrors';

function getQuickBooksEstimateUrl(estimateId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/estimate?txnId=${estimateId}`;
}

export default function ProposalActions({
  onClose,
  saveDraft,
  emailClient,
  onDownloadPDF,
  onDownloadTearSheets,
  saving,
  proposalId,
  projectId,
  quickbooks,
  isLocked,
  onRequestEdit,
  canConvert,
  onConvert,
  onConvertToPO,
  canEdit = true,
}) {
  const token = localStorage.getItem('idToken');
  const [syncingQB, setSyncingQB] = useState(false);
  const [showQBModal, setShowQBModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleProtectedSave = (stayOpen) => {
    if (isLocked) { if (!stayOpen) onClose?.(); return; }
    saveDraft(stayOpen);
  };

  const closeMenu = (fn) => () => { setMenuOpen(false); fn(); };

  async function createQuickBooksProposal() {
    if (!proposalId || !projectId) {
      notify('Please save the proposal before syncing to QuickBooks.');
      return;
    }
    if (!token) { notify('You must be signed in to sync with QuickBooks.'); return; }

    try {
      setSyncingQB(true);
      const res = await fetch(`${API_BASE}/api/quickbooks/create-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ proposalId, projectId }),
      });

      const rawBody = await res.text();
      let data = {};
      try { data = rawBody ? JSON.parse(rawBody) : {}; } catch { data = { error: rawBody || null }; }

      if (!res.ok) {
        if (isQuickBooksConnectionError(data)) {
          setShowQBModal(true);
          return;
        }
        const message = data?.error || data?.details?.error || data?.details?.Fault?.Error?.[0]?.Detail || rawBody || 'Failed to create QuickBooks proposal.';
        if (/already synced/i.test(message)) {
          const updateRes = await fetch(`${API_BASE}/api/quickbooks/update-estimate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ proposalId, projectId }),
          });
          const updateRaw = await updateRes.text();
          let updateData = {};
          try { updateData = updateRaw ? JSON.parse(updateRaw) : {}; } catch { updateData = { error: updateRaw || null }; }
          if (!updateRes.ok) {
            notify(updateData?.error || updateData?.details?.error || updateData?.details?.Fault?.Error?.[0]?.Detail || updateRaw || 'Failed to update QuickBooks proposal.');
            return;
          }
        } else {
          console.error('QuickBooks error:', data);
          notify(message);
          return;
        }
      }

      notify('QuickBooks proposal synced.');
      window.location.reload();
    } catch (err) {
      console.error('QuickBooks sync error:', err);
      notify('QuickBooks sync failed.');
    } finally {
      setSyncingQB(false);
    }
  }

  return (
    <>
      <div className="po-actions">
        <button className="if-btn-secondary" onClick={onClose}>
          Cancel
        </button>

        {canEdit && (
          <button className="if-btn-secondary" onClick={() => handleProtectedSave(true)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        )}

        {canEdit && (
          <button className="if-btn-primary" onClick={() => handleProtectedSave(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save & Close'}
          </button>
        )}

        {/* ── Actions dropdown ── */}
        <div className="prop-actions-menu" ref={menuRef}>
          <button
            className="if-btn-secondary prop-actions-menu__trigger"
            onClick={() => setMenuOpen((v) => !v)}
            disabled={saving}
          >
            Actions <ChevronDown size={14} />
          </button>

          {menuOpen && (
            <div className="prop-actions-menu__dropdown">
              {canEdit && (
                <button onClick={closeMenu(emailClient)}>Send to Client</button>
              )}
              <button onClick={closeMenu(onDownloadPDF)}>Download PDF</button>
              <button onClick={closeMenu(onDownloadTearSheets)}>Download Tear Sheets</button>

              {(canConvert && proposalId) || isLocked ? (
                <div className="prop-actions-menu__divider" />
              ) : null}

              {canConvert && proposalId && (
                <button onClick={closeMenu(onConvert)}>Convert to Invoice</button>
              )}
              {canConvert && proposalId && (
                <button onClick={closeMenu(onConvertToPO)}>Convert to Purchase Order</button>
              )}
              {isLocked && (
                <button onClick={closeMenu(onRequestEdit)}>Edit Proposal</button>
              )}

              <div className="prop-actions-menu__divider" />

              {quickbooks?.estimateId && quickbooks?.environment && (
                <a
                  href={getQuickBooksEstimateUrl(quickbooks.estimateId, quickbooks.environment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                >
                  View in QuickBooks
                </a>
              )}
              <button onClick={closeMenu(createQuickBooksProposal)} disabled={syncingQB}>
                {syncingQB ? 'Syncing...' : 'Sync QuickBooks Proposal'}
              </button>
            </div>
          )}
        </div>
      </div>

      <QuickBooksConnectModal
        open={showQBModal}
        onClose={() => setShowQBModal(false)}
        onConnected={createQuickBooksProposal}
      />
    </>
  );
}
